import {
  attempt,
  first,
  get,
  has,
  isArray,
  isBoolean,
  isEmpty,
  isError,
  isNil,
  isNull,
  isNumber,
  isPlainObject,
  isString,
  lowerCase,
  trim,
  memoize,
  isRegExp,
  omit,
  keys,
  isFunction,
  isNaN,
  truncate,
  isUndefined,
} from 'lodash-es';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { sprintf } from 'sprintf-js';
import { singular } from 'pluralize';
import moment from 'moment';

/// checks if a value is blank
/// a value is considered blank if it is null, undefined, empty string, empty array, empty object, or NaN
export const blank = <T = any>(value: T): boolean => {
  if (isNil(value) || isNull(value)) {
    return true;
  }

  if (isBoolean(value)) {
    return false;
  }

  if (isString(value)) {
    return trim(value).length < 1;
  }

  if (isNumber(value) && isNaN(value)) {
    return true;
  }

  if ((isArray(value) || isPlainObject(value)) && isEmpty(value)) {
    return true;
  }

  return false;
};

/// check if a value is filled
export const filled = <T = any>(value: T): boolean => !blank<T>(value);

/// retrieve a value from a given source based on the given keys
/// if one of the key has a value and exists in the source, it will return the value
/// otherwise it will return the default value if provided or null
export const either = <R = any>(source: any, keys: string[], opts: {
  default_value?: R,
} = {}): R => {
  opts = Object.assign({
    default_value: null,
  }, opts);

  for (const key of keys) {
    const value = get(source, key);

    if (filled(value)) {
      return value;
    }
  }

  return opts.default_value;
}

// retrieve a result when a condition is met. works like a ternary operator
export const when = <R = any>(condition: boolean, opts: {
  then?: () => R,
  else?: () => R,
} = {}): R => {
  opts = Object.assign({
    then: () => null,
    else: () => null,
  }, opts);

  if (condition) {
    return opts.then();
  }

  return opts.else();
}

/// retrieve a result when a value is blank. works like a ternary operator
export const whenBlank = <R = any, T = any>(value: T, opts: {
  then?: () => R,
  else?: () => R,
} = {}) => when(blank(value), opts);

/// retrieve a result when value is filled. works like a ternary operator
export const whenFilled = <R = any, T = any>(value: T, opts: {
  then?: () => R,
  else?: () => R,
} = {}) => when(filled(value), opts);

// returns the value if it is filled, otherwise it will return the fallback value
export const fallback = <R = any>(value: any, opts: {
  /// @deprecated use to
  fallback?: () => R,
  to?: () => R,
}): R => whenFilled(value, {
  then: () => value,
  else: () => (opts.fallback || opts.to)(),
});

/// transform a value based on the given transformation function
export const transform = <T = any, R = any>(value: T, opts: {
  transformer: (value: T) => R,
  default_value?: R,
}): R => {
  if (blank(value)) {
    return opts.default_value;
  }

  return opts.transformer(value);
}

/// checks if a value is in the list using a comparator function
export const isIn = <T = any>(list: T[], opts: {
  comparator: (item: T, pos: number) => boolean,
}): boolean => {
  for (const index in list) {
    if (opts.comparator(list[index], parseInt(index))) {
      return true;
    }
  }

  return false;
}

/// checks if a value is not in the list. works like the opposite of isIn
export const isNotIn = <T = any>(list: T[], opts: {
  comparator: (item: T, pos: number) => boolean,
}) => !isIn(list, opts);

/// check if a given value is a valid system id. it uses the uuid library to validate the id
export const isId = (value: any): boolean => filled(value) && _validateId(value);

// regex to validate uuid
const _validateId = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const data_get = <T extends object, R = any, D = any>(data: T, path: string|string[]|keyof T, opts: {
  default_value?: OptionalValue<D>,
} = {}) : OptionalValue<R> | OptionalValue<D> => {
  opts = Object.assign({
    default_value: null,
  }, opts);

  return get(data, path, opts.default_value);
}

/// a type that can be null or undefined
export type OptionalValue<T> = T | null | undefined;

export const ids_only = (values: any[], using: string = 'id'): string[] => {
  if (blank(values)) {
    return [];
  }

  const output = [];

  for (const value of values) {
    const extracted = data_get(value, using);

    if (blank(extracted) || ! isId(extracted)) {
      continue;
    }

    output.push(extracted);
  }

  return output;
}

/// generate a unique uuid used across the system
// @deprecated: use generate_id
export const generateId = (): string => uuid();

/// pollyfil typescripts Omit Type
/// A type that constructs a new object without the given key(s)
export type Omit<T, K extends keyof any> = Pick<T, Exclude<keyof T, K>>;

/// observes a given observable factory which gives you the ability
/// to perform actions before/after the observable executes
export const observe = <R = any>(opts: {
  observable: () => Observable<R>,
  before?: () => void,
  after?: () => void,
  onError?: (err) => void,
}): Observable<R> => defer(() => {
  if (! isNil(opts.before)) {
    opts.before();
  }

  return opts.observable();
}).pipe(
  take(1),
  finalize(() => {
    if (isNil(opts.after)) {
      return;
    }

    return opts.after();
  }),
  catchError((err) => {
    if (! isNil(opts.onError)) {
      opts.onError(err);
    }

    return throwError(err);
  }),
);

export const is_value_true = (value: any): boolean => {
  if (isBoolean(value)) {
    return value;
  }

  if (isString(value)
    && (
      value == '1'
      || value.toLocaleLowerCase() == 'true'
    )
  ) {
    return true;
  }

  if (isNumber(value) && value != 0) {
    return true;
  }

  return false;
}

export const is_value_false = (value: any): boolean => ! is_value_true(value);

export const filter_array = <T, R extends T>(collection: T[], predicate:  (value: T, index: number) => any): R[] =>
  collection.filter((value, index) => predicate(value, index)) as R[];

export const safely_parse_json = <R = any>(json: any, opts: {
  fallback?: () => R,
} = {}): R | null => {
  if (isPlainObject(json) || isArray(json)) {
    return json;
  }

  opts = Object.assign({
    fallback: () => null,
  }, opts);

  const parsed = attempt((value) => JSON.parse(value), json);

  if (! isError(parsed) && filled(parsed)) {
    return parsed;
  }

  return opts.fallback();
}

export const spf = (format: string, opts: {
  args?: any[],
} = {}): string => sprintf(format, ...opts.args);

export const safely_encode_to_json = (value: any, indentation: string|number|null = null): string | null => {
  if (isString(value)) {
    value = safely_parse_json(value);
  }

  if (blank(value)) {
    return null;
  }

  // Detect JsonableData (object with a toJson method)
  const prepareForJson = (data: any): any => {
    if (data && (typeof data.toJson === 'function')) {
      try {
        return prepareForJson(data.toJson()); // recursively call for nested Jsonables
      } catch {
        return null;
      }
    }

    // Recursively process arrays and objects
    if (Array.isArray(data)) {
      return data.map(prepareForJson);
    }

    if (data && typeof data === 'object') {
      const result: Record<string, any> = {};
      for (const [key, val] of Object.entries(data)) {
        result[key] = prepareForJson(val);
      }
      return result;
    }

    return data;
  };

  const cleaned = prepareForJson(value);
  const encoded = attempt((value) => JSON.stringify(value, null, indentation), cleaned);

  if (isError(encoded)) {
    return null;
  }

  return encoded;
}

export const data_has = <T = any>(value: T, path: string | string[] | keyof T): boolean => has(value, path);

export const arr_count = (value: any): number => {
  if (blank(value) || ! isArray(value)) {
    return 0;
  }

  return value.length;
}

export const str_split = (value: any, args: {
  delimiter: string,
}): string[] => {
  if (isArray(value)) {
    return value;
  }

  if (blank(value) || ! isString(value)) {
    return [];
  }

  return value.split(args.delimiter);
}

export const arr_wrap = <R = any>(value: any): R[] => {
  if (isArray(value)) {
    return value;
  }

  if (blank(value)) {
    return [];
  }

  return [value];
}

export const arr_filter = <T = any>(values: T[], args: {
  predicate: (value: T, index: number) => boolean;
}) : T[] => {
  if (! isArray(values)) {
    return [];
  }

  return values.filter((value, index) => args.predicate(value, index));
}

export const arr_pop = <T = any>(values: T[], args: {
  pos?: number
} = {}): T[] => {
  if (blank(values) || ! isArray(values)) {
    return [];
  }

  args = Object.assign({
    pos: values.length - 1
  }, args);

  if (args.pos < 0) {
    args = Object.assign(args, {
      pos: 0,
    });
  }

  if (args.pos > values.length) {
    return arr_pop(values, {
      pos: values.length - 1,
    });
  }

  /// create a surface copy of list
  const copied = [... values];

  copied.splice(args.pos, 1);

  return copied;
}

export const str_len = <T = any>(value: T): number => {
  if (blank(value) || ! isString(value)) {
    return 0;
  }

  return value.length;
}

export const arr_some = <T = any>(values: T[], predicate: (value: T, index: number) => boolean): boolean => {
  if (blank(values) || ! isArray(values)) {
    return false;
  }

  const filtered = arr_filter(values, {
    predicate: predicate,
  });

  return filled(filtered);
}

export const str_replace = <T = string>(value: T, opts: {
  pattern: string | RegExp,
  with: () => string,
}): string|null  => {
  if (blank(value) || ! isString(value)) {
    return null;
  }

  return value.replace(opts.pattern, opts.with());
}

export const str_snake_case = <T = string>(value: T, opts: {
  lowercase?: boolean,
} = {}): string | null => {
  opts = Object.assign({
    lowercase: false,
  }, opts);

  const cleaned = str_replace(value, {
    pattern: /[^a-z0-9\s]/gi,
    with: () => '',
  });

  const snaked = str_replace(cleaned, {
    pattern: /\s/g,
    with: () => '_'
  });

  if (opts.lowercase && filled(snaked)) {
    return snaked.toLowerCase();
  }

  return snaked;
}

export const arr_map = <R = any, D = any>(values: D[], opts: {
  transformer: (values: D, index: number) => R,
}) => {
  if (blank(values) || ! isArray(values)) {
    return [];
  }

  let output: R[] = [];

  for (let index = 0; index < values.length; index++) {
    output.push(opts.transformer(values[index], index));
  }

  return output;
}

export const is_url = (value): boolean => {
  if (blank(value)) {
    return false;
  }

  if (! isString(value)) {
    return false;
  }

  return /^https:\/\/[a-zA-Z0-9-]+(.[a-zA-Z0-9-]+)+[a-zA-Z0-9-_,&;=?./]+$/
    .test(value);
};

export const str_singular = (value: any): OptionalValue<string> => {
  if (blank(value) || ! isString(value)) {
    return null;
  }

  return singular(value);
}

export const is_file_valid = (file: File, opts: {
  allowed_extensions?: string[],
} = {}): string|null => {
  if (blank(opts.allowed_extensions)) {
    return null;
  }

  const lastDotIndex = file.name.lastIndexOf('.');

  if (lastDotIndex< 0) {
    return 'No file extension extracted.';
  }

  const extension = file.name
    .substring(lastDotIndex + 1)
    .toLowerCase();

  if (blank(extension)) {
    return 'No file extension extracted.';
  }

  if (! opts.allowed_extensions.includes(extension)) {
    return spf('File(s) is not allowed. Extensions allowed are [%s]', {
      args: [opts.allowed_extensions.join(', ')],
    });
  }

  return null;
}

export const arr_first = <T = any>(values: T[], opts: {
  predicate?: (value: T) => boolean,
} = {}): T => {
  if (blank(values)) {
    return null;
  }

  const predicate = opts.predicate;

  if (predicate != undefined) {
    for (const value of values) {
      if (predicate(value)) {
        return value;
      }
    }

    return null;
  }

  return first(values);
};

/// formats a value into a given datetime format
export const format_datetime = (value: any, opts: {
  format: string,
}): OptionalValue<string> => {
  if (blank(value)) {
    return null;
  }

  const parsed = moment(value);

  if (! parsed.isValid()) {
    return null;
  }

  return parsed.format(opts.format);
}

export const map_to_object = <T = Record<string, any>>(map: OptionalValue<Map<string, any>>): T => {
  if (blank(map)) {
    return {} as T;
  }

  const obj = {};

  map.forEach((value, key) => obj[key] = value);

  return obj as T;
}

export const data_is_true = (value: any): boolean => {
  if (blank(value)) {
    return false;
  }

  if (isBoolean(value) && value === true) {
    return true;
  }

  if (isString(value)
    && (value == '1' || lowerCase(value) == 'true')
  ) {
    return true;
  }

  return false;
}

export const data_is_false = (value: any): boolean => ! data_is_true(value);

export const is_interval = (value: any): boolean => {
  if (blank(value) || ! isString(value)) {
    return false;
  }

  return /^[0-9]+[\s_]{1}(?:day|week|month|year)/i.test(lowerCase(value));
}

export const normalize_interval = (value: any): OptionalValue<string> => {
  if (blank(value) || ! isString(value)) {
    return null;
  }

  return lowerCase(value.replace(' ', '_'));
}

export const get_text_color_from_hex = memoize((hex: OptionalValue<string>, args: {
  fallback?: () => string;
} = {}) => {
  args = Object.assign({
    fallback: () => '#ffffff'
  }, args);

  if (blank(hex)) {
    return args.fallback();
  }

  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('')
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

  return luminance > 186 ? '#000000' : '#ffffff';
});

export const arr_reduce = <R extends any, T>(values: T[], args: {
  initial_value: R,
  reducer: (acc: R, curr: T) => R,
}): R => {
  if (blank(values)) {
    return args.initial_value;
  }

  let acc = args.initial_value;

  for (const curr of values) {
    acc = memoize(args.reducer)(acc, curr);
  }

  return acc;
}

export const to_local_datetime = memoize((value: OptionalValue<string|number|Date>): OptionalValue<string> => {
  if (blank(value)) {
    return null;
  }

  const translated = moment.utc(value);

  if (! translated.isValid()) {
    return null;
  }

  return moment(translated.toDate()).format('YYYY-MM-DD HH:mm:ss.SSSS');
});

export const to_utc_datetime = memoize((value: OptionalValue<string|number|Date|'now'>, opts: {
  as_server_compatible?: boolean;
} = {}): OptionalValue<string> => {
  if (blank(value)) {
    return null;
  }

  if (value === 'now') {
    value = Date.now()
  }

  const translated = moment(value);

  if (! translated.isValid()) {
    return null;
  }

  let format = 'YYYY-MM-DD HH:mm:ss.SSSS';

  /// Note: this would only work if you are parsing using the \App\Helpers\DateTime::createDateTimeFromRawData
  if (data_is_true(opts.as_server_compatible)) {
    format = 'YYYY-MM-DD[T]HH:mm:ss.SSSS[Z]';
  }

  if (translated.isUTC()) {
    return translated.format(format);
  }

  return translated.utc().format(format);
});

export const str_contains = (value: OptionalValue<string>, opts: {
  pattern: string | RegExp,
}): boolean => {
  if (blank(value) || ! isString(value)) {
    return false;
  }

  const pattern = opts.pattern;

  if (isRegExp(pattern)) {
    return pattern.test(value);
  }

  return value.includes(pattern);
}

export const data_nullify = (value: any): null => blank(value) ? null : value;

export const map_to_arr = <T = any>(map: OptionalValue<Map<string, any>>): T[] => {
  if (blank(map)) {
    return [];
  }

  const arr = [];

  map.forEach((value, key) => arr.push(value));

  return arr;
};

export const sort_months = (months: string[]): string[] => {
  if (blank(months)) {
    return [];
  }

  const ordering = {
    'jan': 0,
    'feb': 1,
    'mar': 2,
    'apr': 3,
    'may': 4,
    'jun': 5,
    'jul': 6,
    'aug': 7,
    'sep': 8,
    'oct': 9,
    'nov': 10,
    'dec': 11,
  };

  const duplicate = [... months];

  return duplicate.sort((a, b) => {
    const left = lowerCase(a.substring(0, 3));
    const right = lowerCase(b.substring(0, 3));

    return ordering[left] - ordering[right];
  });
}

export const arr_last = <T = any>(values: T[], opts: {
  predicate?: (value: T) => boolean;
}): OptionalValue<T> => {
  if (blank(values)) {
    return null;
  }

  return arr_first([... values].reverse(), {
    predicate: opts.predicate,
  });
}

export const data_omit = <T extends Record<string, any>>(data: T, opts: {
  keys: string[] | keyof T[] | string | keyof T,
}): OptionalValue<Record<string, any>> => {
  if (blank(data)) {
    return null;
  }

  return omit(data, opts.keys);
}

export const data_keys = <T extends Record<string, any>>(data: T): string[] => {
  if (blank(data)) {
    return [];
  }

  return keys(data);
}

export const str_trim = (value: any, opts: {
  chars?: string,
} = {}): string => {
  if (blank(value) || ! isString(value)) {
    return '';
  }

  opts = Object.assign({
    chars: ' '
  }, opts);

  return trim(value, opts.chars);
}

export const str_lower = (value: any): string => {
  if (blank(value)) {
    return '';
  }

  return lowerCase(value);
}

export type Identifiable<T = Record<string, any>> = T & {
  id: string;
};

export const generate_id = (): string => uuid();

export type FieldmagicInputErrors = Record<string, string[]>;

type CommonInputRule = 'required' | 'max' | 'numeric';
type CustomInputRule<D extends Record<string, any>> = (
  value: any,
  key: string,
  data: D,
) => string[];

export const validate_input_data = <D extends Record<string, any>, K extends keyof D>(opts: {
  data: D;
  rules: Record<K, CommonInputRule[] | CustomInputRule<D>[]>;
  config?: {
    max_length?: number;
    messages?: Record<string, Record<string, string>>;
  },
}): FieldmagicInputErrors => {
  opts = Object.assign({
    config: {},
  }, opts);

  const config = Object.assign({
    max_length: 0,
  }, opts.config);

  if (blank(opts.data)) {
    return {};
  }

  if (blank(opts.rules)) {
    return {};
  }

  const errors: FieldmagicInputErrors = {};

  for (const key in opts.data) {
    const value = opts.data[key];
    const rules = data_get(opts.rules, [key]);

    if (blank(rules)) {
      continue;
    }

    /// iterate to rules
    for (const rule of rules) {
      if (isString(rule) && rule === 'required' && blank(value)) {
        errors[key] = [fallback(data_get(config, ['messages', 'required', key]), {
          to: () => 'field_is_required',
        })];

        continue;
      }

      if (isString(rule) && rule === 'max') {
        if (isString(value) && str_len(value) > config.max_length) {
          errors[key] = [spf('This field must not exceed %d characters.', {
            args: [config.max_length],
          })];

          continue;
        }

        if (isArray(value) && arr_count(value) > config.max_length) {
          errors[key] = [spf('This field must not exceed %d items.', {
            args: [config.max_length],
          })];

          continue;
        }
      }

      if (
        isString(rule) &&
        rule === 'numeric' &&
        filled(value) &&
        isNaN(Number(value))
      ) {
        errors[key] = ['This field must be numeric.'];

        continue;
      }

      if (isFunction(rule)) {
        errors[key] = rule(value, key, opts.data);
        continue;
      }
    }
  }

  return errors;
};

export type Stringable = {
  toString: () => any;
};

export const is_string = (value: any): value is string => isString(value);

export const is_callable = (value: any) => isFunction(value);

export const str_truncate = (value: any, opts: {
  max_length: number;
}): OptionalValue<string> => {
  if (blank(value)) {
    return null;
  }

  return truncate(value, {
    omission: '',
  });
}

export const to_local_date = memoize((value: OptionalValue<string|number|Date>): OptionalValue<string> => {
  if (blank(value)) {
    return null;
  }

  const translated = moment.utc(value);

  if (! translated.isValid()) {
    return null;
  }

  return moment(translated.toDate()).format('YYYY-MM-DD HH:mm:ss.SSSS');
});


export const to_native_local_datetime = (value: OptionalValue<string|number|Date>, opts: {
  as_date?: boolean;
} = {}): OptionalValue<moment.Moment> => {
  opts = Object.assign({
    as_date: false,
  }, opts);

  if (blank(value)) {
    return null;
  }

  let parsed: moment.Moment;

  if (opts.as_date) {
    parsed = moment(to_local_date(value));
  } else {
    parsed = moment(to_local_datetime(value));
  }

  if (! parsed.isValid()) {
    return null;
  }

  return parsed;
}

export type FieldmagicNativeDateTime = moment.Moment;

export const data_merge = <T extends Record<string, any>>(data: OptionalValue<T>, opts: {
  updates?: OptionalValue<Record<string, any>>,
} = {}): Record<string, any> => {
  if (blank(data)) {
    return {};
  }

  if (blank(opts.updates) && filled(data)) {
    return data;
  }

  return {
    ... data,
    ... opts.updates,
  };
}

export const data_defaults = <T extends Record<string, any>>(data: T, opts: {
  defaults: Record<string, any>;
}): Record<string, any> => {
  if (blank(data)) {
    return {};
  }

  return Object.assign(opts.defaults, data);
}

/// polyfill to ts generic type Partial
export type PartialData<T extends Record<string, any>> = {
  [K in keyof T]?: T[K];
};

export const is_defined = (value: any): boolean => ! isUndefined(value);

/**
 * Sanitize generated AI email content by removing unwanted placeholder artifacts
 * but keeping Liquid variables like {{ customer_name }} for later parsing
 */
export const sanitize_generated_ai_email = (strText: OptionalValue<string>): string => {
  if (blank(strText)) {
    return '';
  }

  let strSanitized = strText;

  // Remove [Your Name], [Your Company], etc. - these are unwanted placeholders
  strSanitized = strSanitized.replace(/\[[^\]]+\]/g, '');

  // Keep {{ variables }} - these will be parsed when email is sent
  // Remove liquid tags like {% tag %} - these are not needed
  strSanitized = strSanitized.replace(/\{%[^%]+%\}/g, '');

  // Remove any hallucinated Request ID fragments while preserving other content
  // Handles plain text and basic HTML-styled labels
  strSanitized = strSanitized.replace(/\bRequest\s*ID:\s*[^.,<\n]+[.,]?/gi, '');
  strSanitized = strSanitized.replace(/<[^>]*>Request\s*ID:<\/[^>]*>\s*[^.,<\n]+[.,]?/gi, '');

  // Collapse multiple spaces but preserve single spaces around Liquid variables
  strSanitized = strSanitized.replace(/\s+/g, ' ').trim();

  return strSanitized;
}

export const is_text_valid = (value: any): boolean => {
  if (blank(value) || ! is_string(value)) {
    return true;
  }

  return ! /[^\w\. !(){}\[\]\\\"\/:;,?@#\$%\^&\*-=_\+|.<>\`~\']/mi.test(value);
};

export interface JsonableData {
  toJson(): OptionalValue<Record<string, any>>;
}